import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBan,
  FaBuilding,
  FaChevronDown,
  FaEdit,
  FaFileExport,
  FaEllipsisV,
  FaPlus,
  FaTimes,
  FaStore,
  FaUser,
  FaEnvelope,
  FaGlobe,
  FaLayerGroup,
  FaSearch,
  FaFlag,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import OrganizationDetails from "./OrganizationDetails";
import { useNavigate } from "react-router-dom";
import Pagination from "../components/Pagination";
import {
  deleteOrganization,
  disableOrganization,
  enableOrganization,
  getAllOrganizations,
  onboardOrganization,
  updateOrganization,
} from "../api/api";
import { getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import "../styles/UserManagement.css";
import "../styles/OrganizationList.css";

const PAGE_SIZE = 7;

/* Values are sent to the API as-is (industrySector query param / payload field) */
const SECTOR_OPTIONS = [
  { label: "All Industries", value: "all" },
  { label: "Fintech Services", value: "Fintech Services" },
  { label: "Energy Logistics", value: "Energy Logistics" },
  { label: "Retail Commerce", value: "Retail Commerce" },
  { label: "Digital Assets", value: "Digital Assets" },
];

const STATUS_OPTIONS = [
  { label: "Any Status", value: "all" },
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Blocked", value: "blocked" },
];

const DATE_OPTIONS = [
  { label: "All Time", value: "all" },
  { label: "Last 30 Days", value: "last30days" },
];

const STATUS_META = {
  active: { label: "ACTIVE" },
  suspended: { label: "SUSPENDED" },
  blocked: { label: "BLOCKED" },
};

const normalizeOptionValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCompactNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);

const formatCurrencyCompact = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

const parseRegistrationDate = (value) => {
  const parsed = value ? new Date(value) : null;
  const valid = parsed && !Number.isNaN(parsed.getTime());

  if (!valid) {
    return { value: 0, label: "—" };
  }

  return {
    value: parsed.getTime(),
    label: parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
  };
};

const inferSector = (organization) => {
  const explicit = organization.industrySector || organization.sector || organization.businessType;
  if (explicit) {
    const normalized = normalizeOptionValue(explicit);
    const matched = SECTOR_OPTIONS.find((option) => normalizeOptionValue(option.value) === normalized);
    return {
      label: matched?.label || explicit,
      value: matched?.value || explicit,
    };
  }

  return { label: "—", value: "" };
};

const inferStatusKey = (organization) => {
  const rawStatus = String(organization.status || organization.orgStatus || "").toLowerCase();
  if (rawStatus.includes("block") || rawStatus.includes("disable")) return "blocked";
  if (rawStatus.includes("suspend") || rawStatus.includes("review") || rawStatus.includes("pending")) return "suspended";
  if (rawStatus.includes("active")) return "active";

  if (typeof organization.isDisabled === "boolean") {
    return organization.isDisabled ? "blocked" : "active";
  }

  return "active";
};

const inferHealth = (organization, statusKey) => {
  /* Bar fill comes from the numeric healthValue */
  const score = toNumber(organization.healthValue ?? organization.healthScore);

  /* Tone (colour) derived from the numeric score */
  let tone = "stable";
  if (score > 0) {
    if (score >= 80) tone = "stable";
    else if (score >= 45) tone = "warning";
    else tone = "critical";
  } else if (statusKey === "blocked") {
    tone = "critical";
  } else if (statusKey === "suspended") {
    tone = "warning";
  }

  /* Label text comes straight from the API's health string */
  const label =
    (typeof organization.health === "string" && organization.health.trim()) ||
    `${score}% ${tone.charAt(0).toUpperCase() + tone.slice(1)}`;

  return { score, tone, label };
};

const buildOrganizationViewModel = (organization, index) => {
  const id = organization.orgId || organization._id || `org-${index + 1}`;
  const sector = inferSector(organization);
  const statusKey = inferStatusKey(organization);
  const health = inferHealth(organization, statusKey);
  const registration = parseRegistrationDate(
    organization.registrationDate || organization.createdAt || organization.updatedAt
  );
  const merchants = toNumber(organization.totalMerchants || organization.merchantCount || organization.merchants) || 0;
  const volume =
    toNumber(
      organization.volume30Days ??
        organization.totalVolume ??
        organization.transactionVolume ??
        organization.volume
    ) || 0;
  const displayName = organization.orgName || organization.username || `Organization ${index + 1}`;

  return {
    raw: organization,
    orgId: id,
    orgName: displayName,
    initials: String(displayName).slice(0, 1).toUpperCase(),
    sectorLabel: sector.label,
    sectorValue: sector.value,
    statusKey,
    statusLabel: STATUS_META[statusKey]?.label || "ACTIVE",
    healthLabel: health.label,
    healthScore: Math.max(0, Math.min(100, health.score)),
    healthTone: health.tone,
    volumeRaw: volume,
    volumeText: formatCurrencyCompact(volume),
    merchantsRaw: merchants,
    merchantsText: formatCompactNumber(merchants),
    registrationLabel: registration.label,
    registrationValue: registration.value,
  };
};

/* Extract the organization list + total from whatever envelope the API returns */
const extractOrganizations = (res) => {
  const payload = res?.data ?? res;
  const list =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.organizations) && payload.organizations) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(res?.organizations) && res.organizations) ||
    [];
  const total = toNumber(
    res?.total ??
      res?.totalCount ??
      res?.count ??
      payload?.total ??
      payload?.totalCount ??
      payload?.count ??
      res?.pagination?.total ??
      payload?.pagination?.total ??
      res?.pagination?.totalRecords ??
      payload?.pagination?.totalRecords
  ) || list.length;
  return { list, total };
};

const OrganizationList = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [sectorMenuOpen, setSectorMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [dateMenuOpen, setDateMenuOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerOrg, setDrawerOrg] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [createSectorMenuOpen, setCreateSectorMenuOpen] = useState(false);
  const [editSectorMenuOpen, setEditSectorMenuOpen] = useState(false);
  const createSectorMenuRef = useRef(null);
  const editSectorMenuRef = useRef(null);
  const { showSuccess, showError, showInfo } = useToast();
  const sectorMenuRef = React.useRef(null);
  const statusMenuRef = React.useRef(null);
  const dateMenuRef = React.useRef(null);
  const actionsMenuRef = React.useRef(null);

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const userRoles = Array.isArray(user?.roles) ? user.roles.flat() : [];
  const isAdmin = userType === "admin" || userRoles.includes("super_admin");
  const isViewOnly = userRoles.includes("view-only");

  const apiFilters = useMemo(
    () => ({
      search: debouncedSearch,
      industrySector: sectorFilter,
      status: statusFilter,
      registrationDate: dateFilter,
    }),
    [debouncedSearch, sectorFilter, statusFilter, dateFilter]
  );

  /* Debounce the search box before hitting the API */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* Server-side fetch: pagination + filters + search are handled by the API */
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await getAllOrganizations({
          page: page + 1,
          limit: PAGE_SIZE,
          ...apiFilters,
        });
        if (cancelled) return;

        const { list, total } = extractOrganizations(res);
        setOrganizations(list);
        setTotalCount(total);
      } catch (err) {
        console.error("Error fetching organizations:", err);
        if (!cancelled) {
          setOrganizations([]);
          setTotalCount(0);
          showError("Error", err?.response?.data?.message || "Failed to load organizations", 3000);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, apiFilters]);

  useEffect(() => {
    setPage(0);
  }, [sectorFilter, statusFilter, dateFilter, debouncedSearch]);

  useEffect(() => {
    const handleDocMouseDown = (event) => {
      if (sectorMenuRef.current && !sectorMenuRef.current.contains(event.target)) {
        setSectorMenuOpen(false);
      }
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setStatusMenuOpen(false);
      }
      if (dateMenuRef.current && !dateMenuRef.current.contains(event.target)) {
        setDateMenuOpen(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setOpenActionId(null);
      }
      if (createSectorMenuRef.current && !createSectorMenuRef.current.contains(event.target)) {
        setCreateSectorMenuOpen(false);
      }
      if (editSectorMenuRef.current && !editSectorMenuRef.current.contains(event.target)) {
        setEditSectorMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  useEffect(() => {
    if (!sectorMenuOpen && !statusMenuOpen && !dateMenuOpen && !deleteTarget && !drawerOpen && !createDialogOpen && !editDialogOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setSectorMenuOpen(false);
        setStatusMenuOpen(false);
        setDateMenuOpen(false);
        setDeleteTarget(null);
        setDrawerOpen(false);
        setCreateDialogOpen(false);
        setEditDialogOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sectorMenuOpen, statusMenuOpen, dateMenuOpen, deleteTarget, openActionId, drawerOpen, createDialogOpen, editDialogOpen]);

  const refreshOrganizations = async () => {
    try {
      const res = await getAllOrganizations({
        page: page + 1,
        limit: PAGE_SIZE,
        ...apiFilters,
      });
      const { list, total } = extractOrganizations(res);
      setOrganizations(list);
      setTotalCount(total);
    } catch (err) {
      console.error("Error refreshing organizations:", err);
      showError("Error", err?.response?.data?.message || "Failed to refresh organizations", 3000);
    }
  };

  const handleDeleteOrganization = async (id) => {
    try {
      const res = await deleteOrganization(id);
      if (res?.isSuccess) {
        showSuccess("Deleted", "Organization deleted successfully");
        await refreshOrganizations();
      } else {
        showError("Error", res?.message || "Failed to delete organization", 3000);
      }
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Something went wrong while deleting the organization", 3000);
    }
  };

  const handleEnableOrganization = async (id) => {
    try {
      const res = await enableOrganization(id);
      if (res?.isSuccess) {
        showSuccess("Enabled", "Organization enabled successfully");
        await refreshOrganizations();
      } else {
        showError("Error", res?.message || "Failed to enable organization", 3000);
      }
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Something went wrong while enabling the organization", 3000);
    }
  };

  const handleDisableOrganization = async (id) => {
    try {
      const res = await disableOrganization(id);
      if (res?.isSuccess) {
        showSuccess("Disabled", "Organization disabled successfully");
        await refreshOrganizations();
      } else {
        showError("Error", res?.message || "Failed to disable organization", 3000);
      }
    } catch (err) {
      showError("Error", err?.response?.data?.message || "Something went wrong while disabling the organization", 3000);
    }
  };

  const pageOrganizations = useMemo(
    () => organizations.map((organization, index) => buildOrganizationViewModel(organization, index)),
    [organizations]
  );

  const total = totalCount;

  const sectorLabel = SECTOR_OPTIONS.find((option) => option.value === sectorFilter)?.label || "All Industries";
  const statusLabel = STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label || "Any Status";
  const dateLabel = DATE_OPTIONS.find((option) => option.value === dateFilter)?.label || "All Time";

  const clearFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setSectorFilter("all");
    setStatusFilter("all");
    setDateFilter("all");
  };

  const handleExport = async () => {
    let exportRows = [];

    try {
      const res = await getAllOrganizations({
        page: 1,
        limit: 1000,
        ...apiFilters,
      });
      const { list } = extractOrganizations(res);
      exportRows = list.map((organization, index) => buildOrganizationViewModel(organization, index));
    } catch (err) {
      console.error("Error exporting organizations:", err);
      showError("Export", err?.response?.data?.message || "Failed to fetch organizations for export", 3000);
      return;
    }

    if (!exportRows.length) {
      showInfo("Export", "There are no organizations to export", 2200);
      return;
    }

    const lines = [
      ["Organization", "Sector", "Health", "Volume", "Merchants", "Status", "Registration Date"].join(","),
      ...exportRows.map((organization) => (
        [
          organization.orgName,
          organization.sectorLabel,
          organization.healthLabel,
          organization.volumeText,
          organization.merchantsText,
          organization.statusLabel,
          organization.registrationLabel,
        ]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(",")
      )),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "organization-ecosystem.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  /* ── Org name with icon ── */
  const renderOrganizationCell = (organization) => (
    <div className="oe-orgcell">
      <span className={`oe-orgcell__avatar oe-orgcell__avatar--${organization.statusKey}`}>
        {organization.initials || <FaBuilding />}
      </span>
      <div className="oe-orgcell__meta">
        <div className="oe-orgcell__name">{organization.orgName}</div>
        <div className="oe-orgcell__sub">{organization.sectorLabel}</div>
      </div>
    </div>
  );

  /* ── Status badge ── */
  const renderStatusBadge = (organization) => (
    <span className={`oe-status oe-status--${organization.statusKey}`}>{organization.statusLabel}</span>
  );

  /* ── Action buttons ── */
  const renderActions = (organization) => (
    <div className="oe-actions" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="oe-detailsbtn"
        onClick={() =>
          navigate("/dashboard/OrganizationDetails", {
            state: { orgId: organization.orgId, name: organization.orgName },
          })
        }
      >
        View Details
      </button>

      {isAdmin && !isViewOnly ? (
        <div
          className="oe-actions__more"
          ref={openActionId === organization.orgId ? actionsMenuRef : null}
        >
          <button
            type="button"
            className={openActionId === organization.orgId ? "oe-iconbtn open" : "oe-iconbtn"}
            title="More actions"
            aria-haspopup="menu"
            aria-expanded={openActionId === organization.orgId}
            onClick={(e) => {
              e.stopPropagation();
              setSectorMenuOpen(false);
              setStatusMenuOpen(false);
              setDateMenuOpen(false);
              setOpenActionId((cur) => (cur === organization.orgId ? null : organization.orgId));
            }}
          >
            <FaEllipsisV />
          </button>

          {openActionId === organization.orgId ? (
            <div className="um-sortmenu oe-actions__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="um-sortmenu__item"
                onClick={() => {
                  setEditOrg(organization.raw);
                  setEditDialogOpen(true);
                  setOpenActionId(null);
                }}
              >
                Edit Organization
              </button>

              {organization.statusKey === "blocked" ? (
                <button
                  type="button"
                  role="menuitem"
                  className="um-sortmenu__item"
                  onClick={() => {
                    setOpenActionId(null);
                    handleEnableOrganization(organization.orgId);
                  }}
                >
                  Enable Organization
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="um-sortmenu__item"
                  onClick={() => {
                    setOpenActionId(null);
                    handleDisableOrganization(organization.orgId);
                  }}
                >
                  Disable Organization
                </button>
              )}

              <button
                type="button"
                role="menuitem"
                className="um-sortmenu__item"
                onClick={() => {
                  setOpenActionId(null);
                  setDeleteTarget(organization);
                }}
              >
                Delete Organization
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  /* ── Table header ── */
  if (loading) {
    return (
      <div className={`oe-page ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="oe-loading">Loading organizations…</div>
      </div>
    );
  }

  return (
    <div className={`oe-page ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="oe-header">
        <div>
          <h2 className="oe-title">Organization Ecosystem</h2>
          <div className="oe-subtitle">Observe and optimize your institutional partners and merchant networks.</div>
        </div>

        <div className="oe-headeractions">
          <button type="button" className="oe-secondarybtn" onClick={handleExport}>
            <FaFileExport />
            Export
          </button>

          {isAdmin && !isViewOnly ? (
            <button
              type="button"
              className="oe-primarybtn"
              onClick={() => setCreateDialogOpen(true)}
            >
              <FaPlus />
              Create Organization
            </button>
          ) : null}
        </div>
      </div>

      <div className="oe-toolbar">
        <div className="oe-filter oe-filter--search">
          <div className="oe-filter__label">Search</div>
          <div className="um-forminputwrap">
            <FaSearch className="um-forminputwrap__icon" />
            <input
              type="text"
              className="um-adddialog__input"
              placeholder="Search by name, email or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="oe-filter" ref={sectorMenuRef}>
          <div className="oe-filter__label">Industry Sector</div>
          <button
            type="button"
            className={sectorMenuOpen ? "um-sortpill oe-filter__button open" : "um-sortpill oe-filter__button"}
            onClick={() => {
              setSectorMenuOpen((open) => !open);
              setStatusMenuOpen(false);
              setDateMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={sectorMenuOpen}
          >
            <span className="oe-filter__value">{sectorLabel}</span>
            <FaChevronDown className={sectorMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
          </button>
          {sectorMenuOpen ? (
            <div className="um-sortmenu oe-filter__menu" role="menu">
              {SECTOR_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={option.value === sectorFilter ? "um-sortmenu__item active" : "um-sortmenu__item"}
                  onClick={() => {
                    setSectorFilter(option.value);
                    setSectorMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="oe-filter" ref={statusMenuRef}>
          <div className="oe-filter__label">Status</div>
          <button
            type="button"
            className={statusMenuOpen ? "um-sortpill oe-filter__button open" : "um-sortpill oe-filter__button"}
            onClick={() => {
              setStatusMenuOpen((open) => !open);
              setSectorMenuOpen(false);
              setDateMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={statusMenuOpen}
          >
            <span className="oe-filter__value">{statusLabel}</span>
            <FaChevronDown className={statusMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
          </button>
          {statusMenuOpen ? (
            <div className="um-sortmenu oe-filter__menu" role="menu">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={option.value === statusFilter ? "um-sortmenu__item active" : "um-sortmenu__item"}
                  onClick={() => {
                    setStatusFilter(option.value);
                    setStatusMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="oe-filter" ref={dateMenuRef}>
          <div className="oe-filter__label">Registration Date</div>
          <button
            type="button"
            className={dateMenuOpen ? "um-sortpill oe-filter__button open" : "um-sortpill oe-filter__button"}
            onClick={() => {
              setDateMenuOpen((open) => !open);
              setSectorMenuOpen(false);
              setStatusMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={dateMenuOpen}
          >
            <span className="oe-filter__value">{dateLabel}</span>
            <FaChevronDown className={dateMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
          </button>
          {dateMenuOpen ? (
            <div className="um-sortmenu oe-filter__menu" role="menu">
              {DATE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={option.value === dateFilter ? "um-sortmenu__item active" : "um-sortmenu__item"}
                  onClick={() => {
                    setDateFilter(option.value);
                    setDateMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <button type="button" className="oe-clearbtn" onClick={clearFilters}>
          Clear Filters
        </button>
      </div>

      <section className="oe-panel">
        <div className="oe-tableviewport">
          <div className="oe-grid oe-tablehead">
            <div>Organization &amp; Sector</div>
            <div>Health</div>
            <div>Vol. (30D)</div>
            <div>Merchants</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          <div className="oe-tablebody">
            {pageOrganizations.length ? (
              pageOrganizations.map((organization) => (
                <div key={organization.orgId} className="oe-grid oe-rowcard" onClick={() => { setDrawerOrg(organization); setDrawerOpen(true); }}>
                  <div>{renderOrganizationCell(organization)}</div>

                  <div className="oe-healthcell">
                    <div className={`oe-healthtext oe-healthtext--${organization.healthTone}`}>{organization.healthLabel}</div>
                    <div className="oe-healthbar">
                      <div
                        className={`oe-healthbar__fill oe-healthbar__fill--${organization.healthTone}`}
                        style={{ width: `${organization.healthScore}%` }}
                      />
                    </div>
                  </div>

                  <div className="oe-metriccell">
                    <div className="oe-metriccell__value">{organization.volumeText}</div>
                    <div className="oe-metriccell__sub">{organization.registrationLabel}</div>
                  </div>

                  <div className="oe-metriccell">
                    <div className="oe-metriccell__value">{organization.merchantsText}</div>
                    <div className="oe-metriccell__sub">Org ID {organization.orgId}</div>
                  </div>

                  <div>{renderStatusBadge(organization)}</div>

                  <div>{renderActions(organization)}</div>
                </div>
              ))
            ) : (
              <div className="oe-empty">
                <div className="oe-empty__title">No organizations found</div>
                <div className="oe-empty__subtitle">Try changing your search, sector, status, or date filters.</div>
              </div>
            )}
          </div>
        </div>

        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          itemLabel="organizations"
        />

        {drawerOpen ? (
          <button
            type="button"
            className="oe-drawer-scrim"
            aria-label="Close drawer"
            onClick={() => setDrawerOpen(false)}
          />
        ) : null}

        <aside className={drawerOpen ? "oe-drawer open" : "oe-drawer"} aria-hidden={!drawerOpen}>
        {drawerOrg ? (
          <>
            <div className="oe-drawer__header">
              <div className="oe-drawer__kicker">Organization Detail</div>
              <button type="button" className="oe-drawer__close" onClick={() => setDrawerOpen(false)} aria-label="Close drawer">
                <FaTimes />
              </button>
            </div>
            <div className="oe-drawer__user">
              <div className="oe-drawer__avatar">{String(drawerOrg.orgName).slice(0,1)}</div>
              <div>
                <div className="oe-drawer__name">{drawerOrg.orgName}</div>
                <div className="oe-drawer__meta">
                  <span className="oe-drawer__orgid">ID {drawerOrg.orgId}</span>
                </div>
              </div>
            </div>
            <div className="oe-drawer__body">
              <OrganizationDetails
                orgId={drawerOrg.orgId}
                orgName={drawerOrg.orgName}
                isSidebarOpen={isSidebarOpen}
                mode="drawer"
              />
            </div>
            <div className="oe-drawer__footer">
              <button
                type="button"
                className="oe-drawer__btn oe-drawer__btn--ghost"
                onClick={() => {
                  setEditOrg(drawerOrg.raw);
                  setDrawerOpen(false);
                  setEditDialogOpen(true);
                }}
              >
                <FaEdit /> Edit Entity
              </button>
              <button type="button" className="oe-drawer__btn oe-drawer__btn--danger" onClick={() => setDrawerOpen(false)}>
                <FaBan /> Block Access
              </button>
            </div>
          </>
        ) : null}
        </aside>
      </section>

      {deleteTarget ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Delete Organization">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close delete confirmation" onClick={() => setDeleteTarget(null)} />
          <div className="um-confirmdialog">
            <div className="um-confirmdialog__header">
              <h3 className="um-confirmdialog__title">Delete Organization</h3>
              <button type="button" className="um-adddialog__close" onClick={() => setDeleteTarget(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="um-confirmdialog__body">
              <p className="um-confirmdialog__text">
                Are you sure you want to delete <strong>{deleteTarget.orgName}</strong>?
              </p>
              <p className="um-confirmdialog__subtext">
                This action will permanently remove the organization from the ecosystem list.
              </p>
            </div>
            <div className="um-confirmdialog__footer">
              <button type="button" className="um-adddialog__back" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                type="button"
                className="um-confirmdialog__delete"
                onClick={async () => {
                  const targetId = deleteTarget.orgId;
                  setDeleteTarget(null);
                  await handleDeleteOrganization(targetId);
                }}
              >
                Delete Organization
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createDialogOpen ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Create Organization">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close create organization dialog" onClick={() => setCreateDialogOpen(false)} />
          <Formik
            initialValues={{
              orgName: "",
              username: "",
              contact: "92",
              email: "",
              website: "",
              industrySector: "",
              address: {
                country: "Pakistan",
                city: "",
                addressLine: "",
              },
            }}
            validationSchema={Yup.object().shape({
              orgName: Yup.string().required("Organization name is required"),
              username: Yup.string().min(3, "Minimum 3 characters").required("Username is required"),
              contact: Yup.string().matches(/^[0-9]{10,15}$/, "Invalid contact number").required("Contact is required"),
              email: Yup.string().email("Invalid email").required("Email is required"),
              website: Yup.string().required("Website is required"),
              industrySector: Yup.string()
                .oneOf(SECTOR_OPTIONS.filter((o) => o.value !== "all").map((o) => o.value), "Invalid industry sector")
                .required("Industry sector is required"),
              address: Yup.object().shape({
                country: Yup.string().required("Country is required"),
                city: Yup.string().required("City is required"),
                addressLine: Yup.string().required("Address is required"),
              }),
            })}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              try {
                const payload = {
                  orgName: values.orgName,
                  username: values.username,
                  contact: values.contact,
                  email: values.email,
                  website: values.website,
                  industrySector: values.industrySector,
                  address: {
                    country: values.address.country,
                    city: values.address.city,
                    addressLine: values.address.addressLine,
                  },
                };
                const res = await onboardOrganization(payload);
                if (res?.isSuccess) {
                  showSuccess("Created", res?.message || "Organization created successfully");
                  resetForm();
                  setCreateDialogOpen(false);
                  await refreshOrganizations();
                } else {
                  showError("Error", res?.message || "Failed to create organization", 3000);
                }
              } catch (err) {
                console.error("Organization Error:", err);
                showError("Error", err?.response?.data?.message || "Something went wrong!", 3000);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="um-adddialog-shell um-adddialog-shell--overlay">
                <div className="um-adddialog">
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">Create Organization</h2>
                      <div className="um-adddialog__subtitle">Onboard a new institutional partner into the ecosystem.</div>
                    </div>
                    <button type="button" className="um-adddialog__close" onClick={() => setCreateDialogOpen(false)}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="um-adddialog__body">
                    <div className="um-adddialog__grid">
                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-name">Organization Name</label>
                        <div className="um-forminputwrap">
                          <FaStore className="um-forminputwrap__icon" />
                          <Field id="org-name" name="orgName" type="text" className="um-adddialog__input" placeholder="e.g. Apex Financial Group" />
                        </div>
                        {touched.orgName && errors.orgName ? <div className="error-text">{errors.orgName}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-username">Username</label>
                        <div className="um-forminputwrap">
                          <FaUser className="um-forminputwrap__icon" />
                          <Field id="org-username" name="username" type="text" className="um-adddialog__input" placeholder="e.g. apex_fin" />
                        </div>
                        {touched.username && errors.username ? <div className="error-text">{errors.username}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Contact Number</label>
                        <PhoneInput
                          country="pk"
                          value={values.contact}
                          onChange={(value) => setFieldValue("contact", value.replace(/\D/g, ""))}
                          inputClass="um-adddialog__input"
                          countryCodeEditable={false}
                        />
                        {touched.contact && errors.contact ? <div className="error-text">{errors.contact}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-email">Email Address</label>
                        <div className="um-forminputwrap">
                          <FaEnvelope className="um-forminputwrap__icon" />
                          <Field id="org-email" name="email" type="email" className="um-adddialog__input" placeholder="ops@apexfin.com" />
                        </div>
                        {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-website">Website URL</label>
                        <div className="um-forminputwrap">
                          <FaGlobe className="um-forminputwrap__icon" />
                          <Field id="org-website" name="website" type="text" className="um-adddialog__input" placeholder="www.apexfin.com" />
                        </div>
                        {touched.website && errors.website ? <div className="error-text">{errors.website}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Industry Sector</label>
                        <div className="um-formselectwrap" ref={createSectorMenuRef}>
                          <button
                            type="button"
                            className={createSectorMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                            onClick={() => setCreateSectorMenuOpen((open) => !open)}
                            aria-haspopup="menu"
                            aria-expanded={createSectorMenuOpen}
                          >
                            <span className="um-formselect__value">
                              {values.industrySector || "Select Industry Sector"}
                            </span>
                            <FaChevronDown className={createSectorMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                          </button>
                          {createSectorMenuOpen ? (
                            <div className="um-sortmenu um-formselectmenu" role="menu">
                              {SECTOR_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="menuitem"
                                  className={option.value === values.industrySector ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                  onClick={() => {
                                    setFieldValue("industrySector", option.value);
                                    setCreateSectorMenuOpen(false);
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {touched.industrySector && errors.industrySector ? <div className="error-text">{errors.industrySector}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-country">Country</label>
                        <div className="um-forminputwrap">
                          <FaFlag className="um-forminputwrap__icon" />
                          <Field id="org-country" name="address.country" type="text" className="um-adddialog__input" placeholder="Pakistan" />
                        </div>
                        <ErrorMessage name="address.country" component="div" className="error-text" />
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-city">City</label>
                        <div className="um-forminputwrap">
                          <FaMapMarkerAlt className="um-forminputwrap__icon" />
                          <Field id="org-city" name="address.city" type="text" className="um-adddialog__input" placeholder="Karachi" />
                        </div>
                        <ErrorMessage name="address.city" component="div" className="error-text" />
                      </div>

                      <div className="um-adddialog__field um-adddialog__field--full">
                        <label className="um-adddialog__label" htmlFor="org-addressline">Address Line</label>
                        <div className="um-forminputwrap">
                          <FaLayerGroup className="um-forminputwrap__icon" />
                          <Field id="org-addressline" name="address.addressLine" type="text" className="um-adddialog__input" placeholder="Main Shahrah-e-Faisal" />
                        </div>
                        <ErrorMessage name="address.addressLine" component="div" className="error-text" />
                      </div>
                    </div>
                  </div>

                  <div className="um-adddialog__footer">
                    <button type="button" className="um-adddialog__cancel" onClick={() => setCreateDialogOpen(false)}>Cancel</button>
                    <button type="submit" className="um-adddialog__submit" disabled={isSubmitting}>
                      {isSubmitting ? "Creating..." : "Create Organization"}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : null}

      {editDialogOpen && editOrg ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Edit Organization">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close edit organization dialog" onClick={() => { setEditDialogOpen(false); setEditOrg(null); setEditSectorMenuOpen(false); }} />
          <Formik
            initialValues={{
              orgName: editOrg.orgName || "",
              contact: String(editOrg.contact || "92").replace(/\D/g, ""),
              email: editOrg.email || "",
              website: editOrg.website || "",
              industrySector: editOrg.industrySector || "",
              address: {
                country: editOrg.address?.country || "Pakistan",
                city: editOrg.address?.city || "",
                addressLine: editOrg.address?.addressLine || "",
              },
            }}
            validationSchema={Yup.object().shape({
              orgName: Yup.string().required("Organization name is required"),
              contact: Yup.string().matches(/^[0-9]{10,15}$/, "Invalid contact number").required("Contact is required"),
              email: Yup.string().email("Invalid email").required("Email is required"),
              website: Yup.string().required("Website is required"),
              industrySector: Yup.string()
                .oneOf(SECTOR_OPTIONS.filter((o) => o.value !== "all").map((o) => o.value), "Invalid industry sector")
                .required("Industry sector is required"),
              address: Yup.object().shape({
                country: Yup.string().required("Country is required"),
                city: Yup.string().required("City is required"),
                addressLine: Yup.string().required("Address is required"),
              }),
            })}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const payload = {
                  orgName: values.orgName,
                  contact: values.contact,
                  email: values.email,
                  website: values.website,
                  industrySector: values.industrySector,
                  address: {
                    country: values.address.country,
                    city: values.address.city,
                    addressLine: values.address.addressLine,
                  },
                };
                const res = await updateOrganization(editOrg.orgId || editOrg._id, payload);
                if (res?.isSuccess) {
                  showSuccess("Updated", res?.message || "Organization updated successfully");
                  setEditDialogOpen(false);
                  setEditOrg(null);
                  await refreshOrganizations();
                } else {
                  showError("Error", res?.message || "Failed to update organization", 3000);
                }
              } catch (err) {
                console.error("Edit organization error:", err);
                showError("Error", err?.response?.data?.message || "Something went wrong!", 3000);
              } finally {
                setSubmitting(false);
              }
            }}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="um-adddialog-shell um-adddialog-shell--overlay">
                <div className="um-adddialog">
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">Edit Organization</h2>
                      <div className="um-adddialog__subtitle">Update organization details.</div>
                    </div>
                    <button type="button" className="um-adddialog__close" onClick={() => { setEditDialogOpen(false); setEditOrg(null); setEditSectorMenuOpen(false); }}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="um-adddialog__body">
                    <div className="um-adddialog__grid">
                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-name">Organization Name</label>
                        <div className="um-forminputwrap">
                          <FaStore className="um-forminputwrap__icon" />
                          <Field id="edit-org-name" name="orgName" type="text" className="um-adddialog__input" placeholder="e.g. Apex Financial Group" />
                        </div>
                        {touched.orgName && errors.orgName ? <div className="error-text">{errors.orgName}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Contact Number</label>
                        <PhoneInput
                          country="pk"
                          value={values.contact}
                          onChange={(value) => setFieldValue("contact", value.replace(/\D/g, ""))}
                          inputClass="um-adddialog__input"
                          countryCodeEditable={false}
                        />
                        {touched.contact && errors.contact ? <div className="error-text">{errors.contact}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-email">Email Address</label>
                        <div className="um-forminputwrap">
                          <FaEnvelope className="um-forminputwrap__icon" />
                          <Field id="edit-org-email" name="email" type="email" className="um-adddialog__input" placeholder="ops@apexfin.com" />
                        </div>
                        {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-website">Website URL</label>
                        <div className="um-forminputwrap">
                          <FaGlobe className="um-forminputwrap__icon" />
                          <Field id="edit-org-website" name="website" type="text" className="um-adddialog__input" placeholder="www.apexfin.com" />
                        </div>
                        {touched.website && errors.website ? <div className="error-text">{errors.website}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Industry Sector</label>
                        <div className="um-formselectwrap" ref={editSectorMenuRef}>
                          <button
                            type="button"
                            className={editSectorMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                            onClick={() => setEditSectorMenuOpen((open) => !open)}
                            aria-haspopup="menu"
                            aria-expanded={editSectorMenuOpen}
                          >
                            <span className="um-formselect__value">
                              {values.industrySector || "Select Industry Sector"}
                            </span>
                            <FaChevronDown className={editSectorMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                          </button>
                          {editSectorMenuOpen ? (
                            <div className="um-sortmenu um-formselectmenu" role="menu">
                              {SECTOR_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="menuitem"
                                  className={option.value === values.industrySector ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                  onClick={() => {
                                    setFieldValue("industrySector", option.value);
                                    setEditSectorMenuOpen(false);
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {touched.industrySector && errors.industrySector ? <div className="error-text">{errors.industrySector}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-country">Country</label>
                        <div className="um-forminputwrap">
                          <FaFlag className="um-forminputwrap__icon" />
                          <Field id="edit-org-country" name="address.country" type="text" className="um-adddialog__input" placeholder="Pakistan" />
                        </div>
                        <ErrorMessage name="address.country" component="div" className="error-text" />
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-city">City</label>
                        <div className="um-forminputwrap">
                          <FaMapMarkerAlt className="um-forminputwrap__icon" />
                          <Field id="edit-org-city" name="address.city" type="text" className="um-adddialog__input" placeholder="Lahore" />
                        </div>
                        <ErrorMessage name="address.city" component="div" className="error-text" />
                      </div>

                      <div className="um-adddialog__field um-adddialog__field--full">
                        <label className="um-adddialog__label" htmlFor="edit-org-addressline">Address Line</label>
                        <div className="um-forminputwrap">
                          <FaLayerGroup className="um-forminputwrap__icon" />
                          <Field id="edit-org-addressline" name="address.addressLine" type="text" className="um-adddialog__input" placeholder="MM Alam Road" />
                        </div>
                        <ErrorMessage name="address.addressLine" component="div" className="error-text" />
                      </div>
                    </div>
                  </div>

                  <div className="um-adddialog__footer">
                    <button type="button" className="um-adddialog__cancel" onClick={() => { setEditDialogOpen(false); setEditOrg(null); setEditSectorMenuOpen(false); }}>Cancel</button>
                    <button type="submit" className="um-adddialog__submit" disabled={isSubmitting}>
                      {isSubmitting ? "Updating..." : "Update Organization"}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : null}
    </div>
  );
};

export default OrganizationList;
